import { useEffect } from "react";
import { pillPath } from "./pill";
import { isReduced, useReducedMotion } from "./preference";

/**
 * 画面の動きの合図を付ける（参照元：Holoriba-homepage src/components/motion/MotionRoot.tsx を一画面アプリ向けに移植）。
 * - [data-reveal]：画面に入ったら data-shown（上の端が画面の下から12%の線を越えたら）
 * - [data-hl]：大事なところの しるし。読む位置（下から3割の線）に来たら data-drawn。輪はボタンの実際の大きさで描き直す
 * - [data-loop]：ずっと続く動き。画面の外にあるあいだ data-away を付けて止める
 * - 画面の中身は後から入れかわるので、MutationObserver で新しい要素も見る
 * 動きを減らす設定では、隠してから現す演出をしない（html.motion-ready を付けない＝最初から見える）。
 * 参照元にあった「ページを移るときの全面の帯」と視差・りばたんは移していない（docs/REUSE.md）。
 */
export function MotionRoot() {
  const reduced = useReducedMotion();

  useEffect(() => {
    const root = document.documentElement;

    // 輪の線は、ボタンの実際の大きさに合わせて描き直す（引き伸ばすと角の丸みがつぶれる）
    const ringRo = new ResizeObserver((list) => {
      for (const en of list) {
        const box = en.borderBoxSize?.[0];
        const w = Math.round((box?.inlineSize ?? en.contentRect.width) * 10) / 10;
        const h = Math.round((box?.blockSize ?? en.contentRect.height) * 10) / 10;
        if (!w || !h) continue;
        const svg = en.target as SVGSVGElement;
        svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
        const d = pillPath(w, h);
        svg.querySelectorAll("path").forEach((path) => path.setAttribute("d", d));
      }
    });
    const watchRings = (scope: ParentNode) => scope.querySelectorAll(".hl__ring").forEach((svg) => ringRo.observe(svg));
    watchRings(document);

    if (reduced || isReduced()) {
      root.classList.remove("motion-ready");
      const mo = new MutationObserver((list) => list.forEach((m) => m.addedNodes.forEach((n) => n instanceof Element && watchRings(n))));
      mo.observe(document.body, { childList: true, subtree: true });
      return () => {
        ringRo.disconnect();
        mo.disconnect();
      };
    }
    root.classList.add("motion-ready");

    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (en.isIntersecting) {
            (en.target as HTMLElement).dataset.shown = "true";
            io.unobserve(en.target);
          }
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0 },
    );
    const hl = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (en.isIntersecting) {
            (en.target as HTMLElement).dataset.drawn = "true";
            hl.unobserve(en.target);
          }
        }
      },
      { rootMargin: "0px 0px -30% 0px", threshold: 0 },
    );
    const loops = new IntersectionObserver((entries) => {
      for (const en of entries) {
        const el = en.target as HTMLElement;
        if (en.isIntersecting) delete el.dataset.away;
        else el.dataset.away = "true";
      }
    });

    const watch = (scope: ParentNode) => {
      scope.querySelectorAll<HTMLElement>("[data-reveal]:not([data-shown])").forEach((el) => io.observe(el));
      scope.querySelectorAll<HTMLElement>("[data-loop]").forEach((el) => loops.observe(el));
      scope.querySelectorAll<HTMLElement>("[data-hl]:not([data-drawn])").forEach((el) => {
        // 開いたときに画面の中にすっかり収まっているもの（固定プレイヤーの近くのボタンなど）は、3割の線より下でも現す
        const r = el.getBoundingClientRect();
        if (el.offsetHeight > 0 && r.top >= 0 && r.bottom <= window.innerHeight) el.dataset.drawn = "true";
        else hl.observe(el);
      });
      watchRings(scope);
    };
    const watchSelf = (el: Element) => {
      if (el.matches("[data-reveal]:not([data-shown])")) io.observe(el);
      if (el.matches("[data-loop]")) loops.observe(el);
      if (el.matches("[data-hl]:not([data-drawn])")) hl.observe(el);
    };
    watch(document);
    const mo = new MutationObserver((list) => {
      for (const m of list)
        m.addedNodes.forEach((n) => {
          if (!(n instanceof Element)) return;
          watchSelf(n);
          watch(n);
        });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      hl.disconnect();
      loops.disconnect();
      ringRo.disconnect();
      mo.disconnect();
      root.classList.remove("motion-ready");
    };
  }, [reduced]);

  return null;
}
